import React, { Component, Fragment } from "react";
import DatePicker from "./DatePicker";
import { formatDate, diffDates } from "./services/utils";
import { computeStayDatesAuto } from "./services/clubmed";
import { CloseIcon } from "./Icons";

import "./AppConfig.css";

const computeStopDate = (start, stop) => {
  if (stop === undefined) {
    const stopDate = new Date(start);
    stopDate.setDate(stopDate.getDate() + 7);
    return stopDate;
  }
  return stop;
};

const formatFullDate = date =>
  date.toLocaleDateString("fr-FR", {
    weekday: "long",
    month: "long",
    day: "numeric"
  });

export default class AppConfig extends Component {
  state = {
    manageStay: this.props.manageStay,
    start: new Date(this.props.stayFrom),
    stop: new Date(this.props.stayTo),
    duration: this.props.duration,
    confirmReset: false
  };

  computeMinAndMaxDuration = () => {
    const { duration, start, stop, manageStay, min, max } = this.state;

    const { stayFrom, stayTo } = manageStay
      ? { stayFrom: start, stayTo: stop }
      : computeStayDatesAuto(min, max);

    const delta = diffDates(stayFrom, stayTo);
    const minDuration = delta < duration.min ? delta : duration.min;
    const maxDuration = delta;
    if (duration.min !== minDuration || duration.max !== maxDuration) {
      this.setState({ duration: { min: minDuration, max: maxDuration } });
    }
  };

  manageStayChange = value => () => {
    this.setState({ manageStay: value }, () => this.computeMinAndMaxDuration());
  };

  stayChange = stay => {
    const { start, stop: stopDate } = stay;
    const stop = computeStopDate(start, stopDate);
    this.setState({ start, stop }, () => this.computeMinAndMaxDuration());
  };

  durationChange = e => {};

  increaseDuration = name => () => {
    const { duration } = this.state;
    if (name === "max" || duration.min < duration.max) {
      this.setState({ duration: { ...duration, [name]: duration[name] + 1 } });
    }
  };

  decreaseDuration = name => () => {
    const { duration } = this.state;
    if (duration[name] > 1 && (name === "min" || duration.max > duration.min)) {
      this.setState({ duration: { ...duration, [name]: duration[name] - 1 } });
    }
  };

  saveConfig = () => {
    const { saveConfig } = this.props;
    const { start, stop, duration, manageStay } = this.state;

    saveConfig(
      manageStay ? formatDate(start) : undefined,
      manageStay && stop !== undefined ? formatDate(stop) : undefined,
      duration
    );
  };

  confirmResetScreen = () => this.setState({ confirmReset: true });

  cancelReset = () => this.setState({ confirmReset: false });

  renderDatesString(start, stop) {
    return (
      <div className="cm-app-config--date-picker-text">
        Du <span>{formatFullDate(start)}</span> au{" "}
        <span>{formatFullDate(stop)}</span>
      </div>
    );
  }

  renderDatesAuto() {
    const { manageStay } = this.state;
    if (!manageStay) {
      const { min, max } = this.props;
      const dates = computeStayDatesAuto(min, max);
      return this.renderDatesString(dates.stayFrom, dates.stayTo);
    }
  }

  renderModeSelector() {
    const { manageStay } = this.state;
    return (
      <div className="cm-app-config--mode">
        <div className="cm-app-config--mode-selector">
          Dates de séjour
          <div className="cm-app-config--mode-buttons">
            <button
              className={manageStay ? "selected" : undefined}
              onClick={this.manageStayChange(true)}
            >
              Manuelle
            </button>
            <button
              className={!manageStay ? "selected" : undefined}
              onClick={this.manageStayChange(false)}
            >
              Automatique
            </button>
          </div>
        </div>
        {this.renderDatesAuto()}
      </div>
    );
  }

  renderDatePicker() {
    const { start, stop, manageStay } = this.state;
    const { min, max } = this.props;
    if (manageStay) {
      return (
        <div className="cm-app-config--date-picker-outer">
          <div className="cm-app-config--date-picker">
            <DatePicker
              selected={{ start, stop }}
              from={min}
              to={max}
              selectionChanged={this.stayChange}
              rangePicker
            />
          </div>
          {this.renderDatesString(start, stop)}
        </div>
      );
    }
  }

  renderBound(label, name) {
    const { duration } = this.state;
    return (
      <div className="cm-app-config--duration-bound">
        {label}
        <input
          type="text"
          readOnly
          value={duration[name]}
          onChange={this.durationChange}
        />
        <div className="cm-app-config--duration-buttons">
          <div
            className="cm-app-config--duration-increase"
            onClick={this.increaseDuration(name)}
          >
            +
          </div>
          <div
            className="cm-app-config--duration-decrease"
            onClick={this.decreaseDuration(name)}
          >
            -
          </div>
        </div>
      </div>
    );
  }

  renderDuration() {
    return (
      <div className="cm-app-config--duration">
        Nombre de jours :{this.renderBound("min", "min")}
        {this.renderBound("max", "max")}
      </div>
    );
  }

  renderButtons() {
    return (
      <div className="cm-app-config--buttons">
        <button className="cm-app-config--save" onClick={this.saveConfig}>
          Enregistrer
        </button>
        <button
          className="cm-app-config--reset-btn"
          onClick={this.confirmResetScreen}
        >
          Réinitialiser l'application
        </button>
      </div>
    );
  }

  renderConfirmReset = () => (
    <div className="cm-app-config--reset">
      <div className="cm-app-config--title">Réinitialisation application</div>
      <div className="cm-app-config--reset-title">
        Souhaitez-vous confirmer la réinitialisation de l'application ?
      </div>
      <div className="cm-app-config--reset-buttons">
        <button
          className="cm-app-config--reset-cancel"
          onClick={this.cancelReset}
        >
          Annuler
        </button>
        <button
          className="cm-app-config--reset-confirm"
          onClick={this.props.reset}
        >
          Confirmer
        </button>
      </div>
    </div>
  );

  render() {
    return (
      <Fragment>
        <div className="cm-app-config">
          <div className="cm-app-config--inner">
            <div className="cm-app-config--title">
              Configuration de la réservation de cours
            </div>
            {this.renderModeSelector()}
            {this.renderDatePicker()}
            {this.renderDuration()}
            {this.renderButtons()}
            {this.state.confirmReset && this.renderConfirmReset()}
          </div>
        </div>
        <div
          className="cm-app-config--exit"
          onClick={
            this.state.confirmReset ? this.cancelReset : this.props.close
          }
        >
          <CloseIcon />
        </div>
      </Fragment>
    );
  }
}



// WEBPACK FOOTER //
// ./src/AppConfig.js